import {
  spacer4,
  spacer32,
  spacer16,
  spacer20,
  spacer24,
  spacer48,
  spacer56,
  screenLgMin,
  black,
  white,
  screenMdMax,
  screenSmMin,
  screenXsMax,
  spacer8,
  screenXsMin,
} from '@spotify-internal/encore-web';
import React from 'react';
import styled from 'styled-components';
import { BaseImageType } from '../../atoms/Image/image';
import Header from '../../atoms/Header';
import LegalTerms from '../../atoms/LegalTerms';
import Container from '../../atoms/Container';

interface TweakedPremiumHeroWrapperTypes
  extends React.ComponentProps<typeof Container> {
  backgroundSources: BaseImageType['sources'];
  imageNeedFullWidth?: true | false;
  gradientColor: string;
  direction?: string;
}

export const CustomLegalTerms = styled(LegalTerms)`
  a {
    color: #fff;
  }
`;

export const OfferEndsSpan = styled.span`
  background-color: ${white};
  color: var(--inverted-text-base, ${black});
  padding: ${spacer4};
  margin-bottom: ${spacer16};
`;

export const TweakedPremiumHeroWrapper = styled(
  Container,
)<TweakedPremiumHeroWrapperTypes>`
  color: var(--text-base, ${white});
  background-repeat: no-repeat !important;

  /* Article holds the image background while Section will hold the gradient background. 

  This is necessary because stacking the gradient with a static image height & width
  would trim or cut off the background gradient.
  */
  > article {
    width: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    flex-direction: column;

    ${({ imageNeedFullWidth }) => !imageNeedFullWidth && `max-width: 1170px;`}

    /* This implementation for backgroundSources was pulled and reused directly from RiF Upsell*/
    /* fw or regular image */
    /* fallback image */
    background-image: url(${({ backgroundSources }) =>
      backgroundSources.small['1x']});
    /* image-set if supported */
    ${({ backgroundSources }) =>
      backgroundSources.small['2x'] &&
      `
    background-image: image-set(
      url(${backgroundSources.small['1x']})
        1x,
      url(${backgroundSources.small['2x']})
        2x
    );`}
    background-position: bottom;
    background-repeat: no-repeat;
    background-size: ${({ imageNeedFullWidth }) =>
      imageNeedFullWidth ? 'contain' : '250px'};

    /* Desktop */
    @media (min-width: ${screenLgMin}) {
      ${({ backgroundSources }) =>
        backgroundSources.large &&
        `
      /* same as above, but with large assets */
      background-image: url('${backgroundSources.large['1x']}');
      background-image: 
        url('${backgroundSources.large['1x']}');`};

      ${({ backgroundSources }) =>
        backgroundSources.large &&
        backgroundSources.large['2x'] &&
        `
      /* same as above, but with large asset and image-set */
      background-image: 
        image-set(
          url('${backgroundSources.large['1x']}')
            1x,
            url('${backgroundSources.large['2x']}')
            2x
        );`};

      /*position of image*/
      background-position: ${({ direction }) =>
          direction === 'ltr' ? 'right' : 'left'}
        ${({ imageNeedFullWidth }) =>
          !imageNeedFullWidth ? '10% center' : '-70px top'} !important;
    }

    /* Landscape and middle between phone and desktop */
    @media (max-width: ${screenMdMax}) and (min-width: ${screenSmMin}) {
      ${({ backgroundSources }) =>
        backgroundSources.medium &&
        `
      /* fallback image */
      background-image: url('${backgroundSources.medium['1x']}');
      /* adds gradient on top and bottom
      the asset has this gradient, but on bigger screens the
      asset becomes bigger than the height of the container. */
      background-image: 
        url('${backgroundSources.medium['1x']}');
    `};

      ${({ backgroundSources }) =>
        backgroundSources.medium &&
        backgroundSources.medium['2x'] &&
        `
      /* same as above, but with image-set */
      background-image: 
        image-set(
          url('${backgroundSources.medium['1x']}')
            1x,
            url('${backgroundSources.medium['2x']}')
            2x
        );
    `};

      /*sizing and position of image*/
      ${({ imageNeedFullWidth }) =>
        !!imageNeedFullWidth ? `padding:0;margin:0;` : ``}
      background-position: ${({ direction }) =>
        direction === 'ltr' ? 'right' : 'left'}
        ${({ imageNeedFullWidth }) =>
        !imageNeedFullWidth ? '8% center' : 'top'} !important;
      ${({ imageNeedFullWidth }) =>
        !!imageNeedFullWidth
          ? `background-size: 60% auto;`
          : 'background-size:250px;'}
    }

    /*Mobile resolution */
    @media (max-width: ${screenXsMax}) {
      /* sizing and position of image*/
      background-size: ${({ imageNeedFullWidth }) =>
        imageNeedFullWidth ? '100% auto' : '200px'} !important;
      background-position: top
        ${({ imageNeedFullWidth }) => !imageNeedFullWidth && '5%'} center !important;
      padding-top: ${({ imageNeedFullWidth }) =>
        imageNeedFullWidth ? '60vw' : '250px'};
    }
  }
  /* end - article backgrounds */

  /* Radial Gradients start here */
  @media (min-width: ${screenLgMin}) {
    ${({ gradientColor, direction }) =>
      gradientColor && !!direction
        ? `background:linear-gradient(${
            direction === 'ltr' ? '185' : '175'
          }deg, ${
            gradientColor || `#ffd2d7`
          } 1.61%, #121212 39.73%, var(--background-base, #121212) 75.29%);`
        : `background:var(--background-base, #121212)`}
  }

  @media (max-width: ${screenMdMax}) {
    ${({ gradientColor, direction }) =>
      !!gradientColor && !!direction
        ? `background:linear-gradient(${
            direction === 'ltr' ? '185' : '175'
          }deg, ${
            gradientColor || `#ffd2d7`
          } 1.61%, #121212 39.73%, var(--background-base, #121212) 75.29%);`
        : `background:var(--background-base, #121212)`}
  }
  @media (max-width: ${screenXsMax}) {
    ${({ gradientColor, direction }) =>
      !!gradientColor && !!direction
        ? `background: linear-gradient(${
            direction === 'ltr' ? '194' : '166'
          }deg, ${
            gradientColor || `#ffd2d7`
          } 1.61%, #121212 39.73%,var(--background-base,#121212) 69.29%);`
        : `background:var(--background-base, #121212)`}
  }
  /* Radial Gradients end here */
`;

export const Wrapper = styled.div`
  max-width: 1170px;
  width: 100%;
  margin: auto;
  @media (min-width: ${screenLgMin}) {
    min-height: ${screenXsMin};
  }
  @media (min-width: ${screenSmMin}) {
    display: flex;
    align-items: center;
  }
`;

export const MessageWrapper = styled.div<{
  imageNeedFullWidth: boolean | undefined;
}>`
  /* desktop*/
  @media (min-width: ${screenLgMin}) {
    padding: ${spacer56} ${spacer32};
    max-width: 560px;
  }

  /* Landscape and middle between phone and desktop */
  @media (max-width: ${screenMdMax}) and (min-width: ${screenSmMin}) {
    ${({ imageNeedFullWidth }) =>
      !!imageNeedFullWidth
        ? ` 
          padding: 110px ${spacer20} ${spacer20};
          margin-right: auto;
          header{
            max-width:300px;
            h2{
              line-height: 36px;
              font-size:29px;
            }
            h3{
              margin-top:0;
              line-height:${spacer24};
              font-size:${spacer16};
            }
          }`
        : `max-width:55%; padding:${spacer48} ${spacer20}`}
  }

  @media (max-width: ${screenXsMax}) {
    padding: 0 ${spacer32} ${spacer32} ${spacer32};
  }
`;

export const TweakedPremiumHeader = styled(Header)<
  React.ComponentProps<typeof Header>
>`
  -webkit-font-smoothing: antialiased;
  p {
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  margin-bottom: ${spacer16};
  h2,
  h3 {
    text-shadow: 0px ${spacer4} 14px rgba(0, 0, 0, 0.65);
  }

  /* strike through */
  h2 > span {
    position: relative;
    &:before {
      display: block;
      position: absolute;
      content: '';
      height: 0px;
      bottom: 1.3rem;
      left: 0;
      transform: rotate(-13deg);
      width: 101%;
      box-sizing: border-box;
      border-bottom: 4px solid ${white};
      @media (max-width: ${screenMdMax}) {
        bottom: 1.2rem;
      }
      @media (max-width: ${screenXsMax}) {
        border-width: 3px;
        bottom: 1rem;
      }
    }
  }
`;

export const ButtonContainer = styled.div`
  display: block;
  margin: 35px 0 28px;

  a {
    width: 100%;
    box-sizing: border-box;
    text-decoration: none;
  }

  > div > a:focus-visible {
    outline: solid white;
    outline-width: 3px;
    outline-offset: 5px;
  }

  > div {
    > a > div > span {
      text-transform: none;
    }
    &:first-child > a {
      margin-right: 5px;
      > div {
        padding: ${spacer16} ${spacer56};
      }
    }
  }

  @media (max-width: ${screenMdMax}) {
    margin: ${spacer16} 0 0;
  }
  @media (max-width: ${screenXsMax}) {
    margin: ${spacer16} 0;
  }
  @media (min-width: ${screenSmMin}) {
    a {
      width: auto;
    }

    > div {
      &:not(:last-child) {
        margin-right: ${spacer8};
        margin-bottom: ${spacer16};
      }

      display: inline-block;
    }
  }
  @media (max-width: ${screenXsMax}) {
    > div {
      &:first-child {
        margin-bottom: ${spacer16};
      }
    }
  }
`;
